import { useEffect, useState } from 'react';
import { getAdminEmployeeProgress, GetAdminEmployeeProgressOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Search, Users, CheckCircle, Clock, AlertTriangle, ChevronDown, ChevronRight, Award } from 'lucide-react';
import { toast } from 'sonner';

type Employee = GetAdminEmployeeProgressOutputType['employees'][0];
type Enrollment = Employee['enrollments'][0];

const STATUS_COLORS: Record<string, string> = {
  'Assigned': 'bg-muted text-muted-foreground',
  'Learning': 'bg-primary/15 text-primary',
  'Quiz Passed': 'bg-primary/20 text-primary',
  'Assessment Requested': 'bg-orange-500/15 text-orange-600',
  'Course Complete': 'bg-primary/20 text-primary',
  'Further Training': 'bg-destructive/15 text-destructive',
};

function EmployeeCard({ emp }: { emp: Employee }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-4 flex items-center gap-3 touch-manipulation">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
          {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{emp.name}</p>
          <p className="text-xs text-muted-foreground">{emp.role} · {emp.department}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-sm font-bold text-foreground">{emp.completedCourses}/{emp.totalCourses}</p>
            <p className="text-[10px] text-muted-foreground">Complete</p>
          </div>
          {emp.overdueCourses > 0 && (
            <span className="text-xs bg-destructive/15 text-destructive px-2 py-0.5 rounded-full font-medium">{emp.overdueCourses} overdue</span>
          )}
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-border pt-3">
          {emp.enrollments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No course enrolments</p>
          ) : emp.enrollments.map(e => (
            <div key={e.id} className="flex items-center gap-3 p-2.5 bg-muted/30 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{e.courseTitle}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[e.status] || 'bg-muted text-muted-foreground'}`}>{e.status}</span>
                  {e.hasCertificate && <Award className="w-3 h-3 text-primary" />}
                  {e.dueDate && e.status !== 'Course Complete' && (
                    <span className={`text-[10px] ${new Date(e.dueDate) < new Date() ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                      Due: {new Date(e.dueDate).toLocaleDateString('en-GB')}
                    </span>
                  )}
                </div>
              </div>
              <div className="w-16 shrink-0">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-1.5 bg-primary rounded-full" style={{ width: `${e.progressPercent}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground text-right mt-0.5">{e.progressPercent}%</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmployeeProgressSection() {
  const [data, setData] = useState<GetAdminEmployeeProgressOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getAdminEmployeeProgress({})
      .then(setData)
      .catch(() => toast.error('Failed to load progress data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      {[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}
    </div>
  );

  const employees = (data?.employees || []).filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q);
  });

  const totalComplete = employees.reduce((s, e) => s + e.completedCourses, 0);
  const totalOverdue = employees.reduce((s, e) => s + e.overdueCourses, 0);
  const totalEnrollments = employees.reduce((s, e) => s + e.totalCourses, 0);

  return (
    <div>
      <h2 className="text-lg font-bold text-foreground flex items-center gap-2 mb-4">
        <Users className="w-5 h-5" /> Employee Progress
      </h2>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-foreground">{totalEnrollments}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Enrolments</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-primary">{totalComplete}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Complete</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className={`text-2xl font-bold ${totalOverdue > 0 ? 'text-destructive' : 'text-foreground'}`}>{totalOverdue}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Overdue</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
        <Input placeholder="Search employees…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
      </div>

      {/* Employee list */}
      <div className="space-y-2">
        {employees.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No employees with training enrolments</p>
          </div>
        ) : employees.map(e => (
          <EmployeeCard key={e.id} emp={e} />
        ))}
      </div>
    </div>
  );
}
